import { Engine } from 'engine/Engine';
import { Transform } from 'engine/Transform';
import { ColliderFacet, ColliderType } from 'engine/facets/ColliderFacet';
import { Facet } from 'engine/facets/Facet';
import { SpriteGroupFacet } from 'engine/facets/SpriteGroupFacet';
import React from 'react';
import { Entity } from '../Entity';
import { SpriteFacet } from '../facets/SpriteFacet';
import { StaticSpriteFacet } from '../facets/StaticSpriteFacet';

const getSvgTransform = (t: Transform): string => {
  return `translate(${t.position.join(' ')}) scale(${t.scale.join(' ')}) rotate(${t.rotation})`;
};

const renderAABB = (c: ColliderFacet, key: string | number) => {
  const color = c.props.trigger ? 'cyan' : 'red';
  return (
    <rect
      key={key}
      fill={color}
      x={c.aabb.position[0]}
      y={c.aabb.position[1]}
      width={c.aabb.size[0]}
      height={c.aabb.size[1]}
      opacity="0.2"
    />
  );
};

const renderColliderType = (c: ColliderFacet, key: string | number): JSX.Element => {
  const color = c.props.trigger ? 'cyan' : 'red';
  switch (c.props.type) {
    case ColliderType.Box: return (
      <rect
        key={key}
        fill="none"
        stroke={color}
        x={c.props.offset[0]}
        y={c.props.offset[1]}
        width={c.props.size[0]}
        height={c.props.size[1]}
      />
    );
    case ColliderType.Circle: return (
      <circle
        key={key}
        fill="none"
        stroke={color}
        cx={c.props.offset[0] + c.props.radius}
        cy={c.props.offset[1] + c.props.radius}
        r={c.props.radius}
      />
    );
    case ColliderType.Polygon: return (
      <polygon
        key={key}
        fill="none"
        stroke={color}
        points={c.props.points.join(' ')}
      />
    );
  }
};

const ColliderDebug: React.FC<{ entities: Entity[] }> = ({ entities }) => (
  <>
    {entities.map((e, i) => (
      <circle key={i} cx={e.transform.position[0]} cy={e.transform.position[1]} r={5} />
    ))}
    {entities.map((entity, i) => {
      const colliders = entity.getFacets(ColliderFacet);
      if (!colliders.length) return;
      return (
        <g key={i} transform={getSvgTransform(entity.transform)} transform-origin="center">
          {colliders.map(renderAABB)}
          {colliders.map(renderColliderType)}
        </g>
      );
    })}
  </>
);

interface SvgRendererProps {
  engine: Engine,
  showColliders?: boolean,
}

const renderSprites = (facets: Facet<unknown>[]) => {
  const children: JSX.Element[] = [];
  facets.forEach((s, i) => {
    if (s instanceof SpriteFacet) {
      children.push((
        <g
          key={i}
          transform={s.props.offset && `translate(${s.props.offset.join(' ')})`}
          opacity={s.props.opacity}
        >
          {s.props.frameset.frames[s.frame % s.props.frameset.frames.length]}
        </g>
      ));
    } else if (s instanceof StaticSpriteFacet) {
      children.push((
        <g
          key={i}
          transform={s.props.offset && `translate(${s.props.offset.join(' ')})`}
          opacity={s.props.opacity}
        >
          {s.props.frame}
        </g>
      ));
    } else if (s instanceof SpriteGroupFacet) {
      children.push((
        <g
          key={i}
          transform={getSvgTransform(s.transform)}
          transform-origin={s.props.transformOrigin || 'center'}
          filter={s.props.filter}
        >
          {renderSprites(s.props.children)}
        </g>
      ));
    }
  });
  return children;
};

export class SvgRenderer extends React.Component<SvgRendererProps> {
  render() {
    const {
      engine,
      showColliders,
    } = this.props;
    const [width, height] = engine.viewport;
    const [x, y] = engine.camera.position;
    const { entities, camera } = engine;
    const cameraTransform = [
      `scale(${camera.scale})`,
      `translate(${-x + width / 2} ${-y + height / 2})`,
    ].join(' ');
    const sortedEntities = [...entities].sort(camera.renderOrderComparator);

    return (
      <g transform={cameraTransform} transform-origin="center" className="engine--svg-camera">
        {sortedEntities.map(entity => {
          const children = renderSprites(entity.facets);
          if (!children.length) return;
          return (
            <g key={entity.id} transform={getSvgTransform(entity.transform)} transform-origin="center">
              {children}
            </g>
          );
        })}
        {showColliders && <ColliderDebug entities={entities} />}
      </g>
    );
  }
}
